import cn from 'classnames';
import { CheckIcon, LoaderIcon, XIcon } from '@components/Icons';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import {
  Section,
  SettingsButton,
  SettingsSectionBox,
  SettingsSectionLabel,
  SettingsSectionRow,
} from '@views/Settings/components';
import React, { CSSProperties, useRef, useState } from 'react';
import styles from './ManageWorkspaces.module.scss';
import Workspace, { WorkspaceAPIItem } from '@models/Workspace';
import { apiRequest } from '@support/api';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useDispatch } from 'react-redux';
import { alertUser } from '@actions/alerts';

const confirmWorkspaceRemoval = (workspace: Workspace, onConfirm: () => void) => {
  window.app.confirm(
    {
      title: 'Remove Workspace',
      message: `You are about to remove the workspace ${workspace}. Are you sure?`,
    },
    (confirmed?: boolean) => {
      if (confirmed) {
        onConfirm();
      }
    },
  );
};

function WorkspaceRow({ workspace }) {
  const dispatch = useDispatch();
  const { workspaces, updateWorkspaces } = useWorkspaceContext();
  const [saving, setSaving] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(workspace.name);

  const deleteWorkspace = () => {
    confirmWorkspaceRemoval(workspace, () => {
      // we filter instead of delete by key here b/c we key off of temp IDs when adding workspaces
      updateWorkspaces(workspaces.filter(({ id }) => id !== workspace.id).toOrderedMap());
      apiRequest('DELETE', `/workspaces/${workspace.id}`, undefined, { apiVersion: 'v2' })
        .then(() => {
          dispatch(alertUser(`Removed workspace ${workspace.name}.`, 'success'));
        })
        .catch((err) => {
          dispatch(alertUser(`Failed to remove workspace ${workspace.name}.`, 'error'));
          updateWorkspaces(workspaces);
          console.error(err);
        });
    });
  };

  const saveWorkspaceName = () => {
    if (saving || workspaceName === workspace.name) {
      return;
    }

    setSaving(true);
    const originalName = workspace.name;
    const newName = workspaceName.trim();
    updateWorkspaces(workspaces.update(workspace.id, (w) => w.copyWith({ name: newName })));
    apiRequest('PATCH', `/workspaces/${workspace.id}`, { name: newName }, { apiVersion: 'v2' })
      .catch((err) => {
        console.error(err);
        dispatch(alertUser(`Failed to update workspace name.`, 'error'));
        updateWorkspaces(
          workspaces.update(workspace.id, (w) => w.copyWith({ name: originalName })),
        );
      })
      .finally(() => setSaving(false));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveWorkspaceName();
    }

    if (e.key === 'Escape') {
      e.stopPropagation();
      setWorkspaceName(workspace.name);
    }
  };

  let actions = (
    <button onClick={deleteWorkspace} className={styles.btn}>
      <XIcon width={12} height={12} hover hoverOpacity />
    </button>
  );

  if (!workspace.isPersisted || saving) {
    actions = <LoaderIcon />;
  }

  return (
    <SettingsSectionRow className={styles.row}>
      <div
        className={styles.workspaceAvatar}
        style={
          {
            '--avatar-url': `url(${workspace.avatarURL})`,
          } as CSSProperties
        }
      />

      {/* {workspace.name} */}
      <input
        type="text"
        className={styles.nameInput}
        value={workspaceName}
        onChange={(e) => setWorkspaceName(e.target.value)}
        onBlur={saveWorkspaceName}
        onKeyDown={onKeyDown}
      />

      {actions}
    </SettingsSectionRow>
  );
}

export default function ManageWorkspaces() {
  const dispatch = useDispatch();
  const { workspaces, updateWorkspaces } = useWorkspaceContext();
  const addWorkspaceRowRef = useRef(null);
  const [inputingNewWorkspaceName, setInputingNewWorkspaceName] = useState(false);
  const [savingNewWorkspace, setSavingNewWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  const addWorkspace = () => {
    if (savingNewWorkspace) {
      return;
    }

    const placeholderWorkspace = new Workspace({ name: newWorkspaceName });
    const workspacesWithPlaceholder = workspaces.set(placeholderWorkspace.id, placeholderWorkspace);

    setSavingNewWorkspace(true);
    setInputingNewWorkspaceName(false);
    setTimeout(() => updateWorkspaces(workspacesWithPlaceholder), 0);
    apiRequest<WorkspaceAPIItem>(
      'POST',
      '/workspaces/',
      { name: newWorkspaceName },
      { apiVersion: 'v2' },
    )
      .then((newWorkspaceData) => {
        updateWorkspaces(
          workspacesWithPlaceholder.update(placeholderWorkspace.id, (workspace) =>
            workspace.copyWith(newWorkspaceData),
          ),
        );
      })
      .catch((err) => {
        console.error('failed to create workspace', err);
        updateWorkspaces(workspaces);
        dispatch(alertUser(`Failed to add workspace "${newWorkspaceName}"`, 'error'));
      })
      .finally(() => {
        setSavingNewWorkspace(false);
        setNewWorkspaceName('');
      });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && newWorkspaceName.length > 0) {
      addWorkspace();
    }

    if (e.key === 'Escape' && newWorkspaceName.length !== 0) {
      e.stopPropagation();
      setNewWorkspaceName('');
    }

    if (e.key === 'Escape' && newWorkspaceName.length === 0) {
      e.stopPropagation();
      setInputingNewWorkspaceName(false);
    }
  };

  return (
    <Section>
      <SettingsSectionLabel>Workspaces</SettingsSectionLabel>
      <SettingsSectionBox>
        <TransitionGroup>
          {workspaces
            .entrySeq()
            .toArray()
            .map(([key, workspace]) => (
              <CSSTransition
                key={key}
                timeout={workspace.isPersisted ? 300 : 0}
                classNames={{
                  exit: styles.exit,
                  exitActive: styles.exitActive,
                }}
              >
                <WorkspaceRow workspace={workspace} />
              </CSSTransition>
            ))}
        </TransitionGroup>
        <CSSTransition
          in={inputingNewWorkspaceName}
          nodeRef={addWorkspaceRowRef}
          timeout={savingNewWorkspace ? 0 : 50}
          classNames={{
            exit: styles.exit,
            exitActive: styles.exitActive,
          }}
          unmountOnExit
        >
          <SettingsSectionRow ref={addWorkspaceRowRef} className={styles.addWorkspaceRow}>
            <input
              type="text"
              className={styles.nameInput}
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Enter workspace name"
              autoFocus
            />
            <div className={styles.actions}>
              <button
                onClick={addWorkspace}
                className={cn(styles.btn, styles.submitBtn)}
                disabled={newWorkspaceName.length === 0 || savingNewWorkspace}
              >
                <CheckIcon width={12} height={12} />
              </button>
              <button onClick={() => setInputingNewWorkspaceName(false)} className={cn(styles.btn)}>
                <XIcon width={12} height={12} hover hoverOpacity />
              </button>
            </div>
          </SettingsSectionRow>
        </CSSTransition>
      </SettingsSectionBox>
      {!inputingNewWorkspaceName && !savingNewWorkspace && (
        <SettingsButton
          className={styles.addWorkspaceBtn}
          onClick={() => setInputingNewWorkspaceName(true)}
        >
          Add Workspace
        </SettingsButton>
      )}
    </Section>
  );
}
